'use client';

import { memo, useEffect, useState } from 'react';
import { Input } from '../../input';
import type { FilterStructure } from '../types';

interface TextInputProps {
    filterOption: FilterStructure;
    value: string;
    setValue: (value: string) => void;
    placeholder: string;
    disabled?: boolean;
    dataTestId?: string;
}

/**
 * Componente para filtros de texto libre
 * Utiliza el Input de shadcn con soporte para configuración personalizada
 * Solo aplica el filtro cuando se pierde el foco o se presiona Enter
 */
const TextInput = memo(({
    filterOption,
    value,
    setValue,
    placeholder,
    disabled = false,
    dataTestId,
}: TextInputProps) => {
    const config = filterOption.textConfig;

    // Estado local para el valor del input mientras el usuario escribe
    const [localValue, setLocalValue] = useState(value);

    // Sincronizar estado local cuando cambie el valor externo
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Aplicar el filtro cuando se termine de escribir
    const applyFilter = () => {
        if (localValue !== value) {
            setValue(localValue);
        }
    };

    // Manejar Enter
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            applyFilter();
        }
    };

    // Manejar pérdida de foco
    const handleBlur = () => {
        applyFilter();
    };

    return (
        <Input
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={config?.placeholder || placeholder}
            disabled={disabled}
            data-testid={dataTestId}
            maxLength={config?.maxLength}
            pattern={config?.pattern}
            className="h-10"
        />
    );
});

TextInput.displayName = 'TextInput';

export default TextInput; 